import { useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch } from './useAppSelector';
import { adminLogout } from '../../store/slices/adminAuthSlice';
import { adminService } from '../services/adminService';

/**
 * Hook personalizado para validar el token de administrador
 * Valida el token antes de cada acción y redirige al login si ha expirado
 */
export const useAdminTokenValidation = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const isCheckingRef = useRef(false);

  // Función para validar el token
  const validateToken = useCallback(async () => {
    const token = localStorage.getItem('adminToken');
    
    if (!token) {
      // No hay token, redirigir al login
      dispatch(adminLogout());
      navigate('/admin');
      return false;
    }

    try {
      // Evitar múltiples verificaciones simultáneas
      if (isCheckingRef.current) {
        return true;
      }
      
      isCheckingRef.current = true;
      
      // Verificar el token con el backend
      await adminService.verifyToken(token);
      
      isCheckingRef.current = false;
      return true;
    } catch (error) {
      isCheckingRef.current = false;
      
      // Token inválido o expirado
      console.warn('Token de administrador inválido:', error.message);
      
      // Limpiar datos y redirigir
      dispatch(adminLogout());
      navigate('/admin');
      return false;
    }
  }, [dispatch, navigate]);

  // Función para ejecutar una acción con validación previa del token
  const executeWithTokenValidation = useCallback(async (action) => {
    const isTokenValid = await validateToken();
    
    if (!isTokenValid) {
      throw new Error('Token de administrador inválido');
    }
    
    return action();
  }, [validateToken]);

  // Validar token al montar el componente
  useEffect(() => {
    validateToken();
  }, [validateToken]);

  return {
    validateToken,
    executeWithTokenValidation
  };
}; 